import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getGlobalStates } from "userful-chronos-app-common-js/dist/globalstates/globalStates";
import {
    getMfaConfigFromServer as requestMfaConfig,
    updateMfaConfigOnServer as requestUpdateMfaConfig,
    resetUserOtpFlow,
} from "./msgs/mfaCallers";
import { KeycloakUserID } from "userful-chronos-app-common-js/dist/models/usermgt/usermgt";

export type ResponseSeverity = "SUCCESS" | "COMPOSITE" | "INFO" | "WARN" | "FATAL" | "CHILD" | "UNKNOWN";

export interface OtpLookAroundWindow {
    window: number;
}

export interface OtpTokenPeriod {
    tokenPeriodSec: number;
}

export interface OtpInitialCounter {
    initialCounter: number;
}

export type OtpPolicyDigit = "SIX_DIGIT" | "EIGHT_DIGIT";
export type OtpPolicyType = "TIME_BASED" | "COUNTER_BASED";
export type OtpPolicyAlgorithm = "SHA1" | "SHA256" | "SHA512";

export interface OtpPolicy {
    policyDigits: OtpPolicyDigit;
    otpLookAheadWindow: OtpLookAroundWindow;
    otpTokenPeriod: OtpTokenPeriod;
    otpInitialCounter: OtpInitialCounter;
    otpPolicyType: OtpPolicyType;
    otpPolicyAlgorithm: OtpPolicyAlgorithm;
}

export interface OtpConfig {
    configureOtp: boolean;
    otpPolicy: OtpPolicy;
}

export interface EmailOtpConfig {
    enable: boolean;
    policyDigits: OtpPolicyDigit;
    otpTokenPeriod: OtpTokenPeriod;
}

export interface MFAConfig {
    otpConfig: OtpConfig;
    emailOtpConfig: EmailOtpConfig;
}

export interface MfaState {
    config: MFAConfig | null;
    loading: boolean;
    saving: boolean;
    error: string | null;
}

const initialState: MfaState = {
    config: null,
    loading: false,
    saving: false,
    error: null,
};

export const mfaSlice = createSlice({
    name: "mfaSlice",
    initialState,
    reducers: {
        getMfaConfigFromServer: (state, action: PayloadAction<{}>) => {
            state.loading = true;
            state.error = null;
            const token = getGlobalStates().keycloak?.token;
            if (token) {
                requestMfaConfig(token);
            } else {
                state.loading = false;
            }
        },
        updateMfaConfigOnServer: (
            state,
            action: PayloadAction<{ otpConfig: OtpConfig; emailOtpConfig: EmailOtpConfig }>,
        ) => {
            state.saving = true;
            state.error = null;
            const token = getGlobalStates().keycloak?.token;
            if (token) {
                requestUpdateMfaConfig(action.payload, token);
            } else {
                state.saving = false;
            }
        },
        enableUserOtpFlow: (state, action: PayloadAction<KeycloakUserID>) => {
            state.loading = true;
            state.error = null;
            const token = getGlobalStates().keycloak?.token;
            if (token) {
                resetUserOtpFlow(action.payload, token);
            } else {
                state.loading = false;
            }
        },
        setMfaConfig: (state, action: PayloadAction<MFAConfig>) => {
            state.config = action.payload;
            state.error = null;
        },
        setMfaLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setMfaSaving: (state, action: PayloadAction<boolean>) => {
            state.saving = action.payload;
        },
        setMfaError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
    },
});

export const mfaSliceActions = mfaSlice.actions;
export default mfaSlice.reducer;
